// One mutual separation: the contract basis and why, what happens next, step 1's
// record, arrears and cash, the approver's decisions, the figures with their
// sources, the gates, the four review steps, the document and the dated events.
// Every rule is enforced in Postgres; this screen shows the refusal sentence
// when one comes back, and never computes a figure.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowDown, ArrowLeft, CheckCircle2, FileText, Printer, RefreshCw, Send, XCircle } from 'lucide-react'
import {
  useAttachSeparationInspection, useCheckSeparationSources, useCloseSeparation, useReadSeparationLeave, useComputeSeparation, useDecideSeparationApprover, useDecideSeparationStep,
  useDeliverSeparation, useGetSeparation, useIssueSeparation, useOpenSeparationDocument, useRecordSeparationArrears,
  useRecordSeparationCash, useRecordSeparationUi19Told, useReopenSeparation, useSendSeparationForSignature, useSyncSeparationSignatures, useUpdateSeparation,
} from '../../hooks/backend/separations'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { writeErrorMessage } from '../../utils/writeError'
import {
  BASIS_LABEL, GATE_SECTION, LINE_LABEL, STATUS_LABEL, STEP1_FIELDS, STEP1_GROUPS, failing, firstSentence, formFrom, formatDay, formatWhen,
  formatZar, lineAmount, raisedInAllegationMeeting,
  fullFigures, patchFrom, yourTurn, type Detail, type Review,
} from '../../utils/separation'

// recompute: the change can move a figure, so the figures are computed again
// straight after, rather than waiting for someone to notice and press the button.
type Run = (label: string, fn: () => Promise<unknown>, recompute?: boolean) => Promise<boolean>

export function SeparationDetail({ id, onBack, onSummary, onChanged }: { id: string; onBack: () => void; onSummary: () => void; onChanged: () => void }) {
  const q = useGetSeparation<Detail>()
  const load = q.trigger
  const [busy, setBusy] = useState<string | null>(null)
  const compute = useComputeSeparation<{ ok: boolean; status: string }>()
  const refresh = useCallback(() => { void load({ id }, { skipCache: true }).catch(() => undefined) }, [load, id])
  useEffect(() => { refresh() }, [refresh])

  const run: Run = useCallback(async (label, fn, recompute) => {
    setBusy(label)
    try {
      const r = (await fn()) as { next?: string; reason?: string } | undefined
      toast.success(r?.next ? `${label}. ${r.next}` : label)
      if (recompute) {
        setBusy('Figures computed')
        try {
          const c = await compute.trigger({ id }).result
          toast.success(c.status === 'ok' ? 'Figures computed again' : 'Figures computed again; something is still missing, see "What is left"')
        } catch (e) {
          // Only the step 1 owner, the payroll checker or an admin may compute; anyone else just sees the old figures.
          toast.message(firstSentence(writeErrorMessage(e)))
        }
      }
      refresh(); onChanged()
      return true
    } catch (e) {
      // A refusal can list several things; the toast says the first, and the
      // full list is "What is left" at the top of the page.
      const msg = writeErrorMessage(e)
      toast.error(msg.length > 160 ? `${firstSentence(msg)} The full list is under "What is left" at the top.` : msg)
      return false
    } finally {
      setBusy(null)
    }
  }, [refresh, onChanged, compute, id])

  const d = q.data
  if (q.error && !d) return <p className="p-6 text-sm text-rose-700 dark:text-rose-300">{q.error}</p>
  if (!d || d.case.id !== id) return <p className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading the separation…</p>

  const c = d.case
  const step = (n: number) => d.reviews.find((r) => r.step === n)
  const owner1 = step(1)?.assignee_id === d.me
  const canStep1 = c.status === 'preparing' && (owner1 || d.access.is_admin)

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl space-y-4 p-4 sm:p-5">
        <header className="space-y-2">
          <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs text-muted-foreground md:hidden"><ArrowLeft className="h-3 w-3" /> All separations</button>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-foreground">{d.driver_name || 'Driver'}</h2>
            <span className="text-sm text-muted-foreground">{c.case_ref}</span>
            <Chip tone={c.status === 'signed' ? 'green' : ['declined', 'cancelled'].includes(c.status) ? 'neutral' : 'amber'}>{STATUS_LABEL[c.status as keyof typeof STATUS_LABEL]}</Chip>
            <Chip tone={(c.basis ?? d.basis_live.basis) === 'manual_review' ? 'red' : 'neutral'}>{BASIS_LABEL[(c.basis ?? d.basis_live.basis) as keyof typeof BASIS_LABEL]}{c.basis ? ', frozen' : ''}</Chip>
            {d.suspension.suspended && (
              <Chip tone={(d.suspension.working_days_left ?? 0) <= 2 ? 'red' : 'amber'}>
                Suspended: hearing by {formatDay(d.suspension.hearing_by)}, {d.suspension.working_days_left} working days left
              </Chip>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Offboarding case {d.offboarding.case_ref} · last working day {formatDay(d.offboarding.last_working_day)} ·
            Termination Date {formatDay(c.termination_date)} · delivered {c.delivered_at ? formatWhen(c.delivered_at) : 'not yet'}
          </p>
          <YourTurn d={d} run={run} busy={busy} />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={onSummary}><Printer className="mr-1 h-3.5 w-3.5" /> Review summary</Button>
            <DocumentButton id={id} status={c.status} />
          </div>
        </header>

        <WhatIsLeft d={d} run={run} busy={busy} />
        <BasisCard d={d} />
        <Step1Form d={d} editable={canStep1} run={run} busy={busy} />
        <InspectionLeavePanel d={d} editable={c.status === 'preparing' && (owner1 || d.access.is_admin || d.access.is_figures_checker)} run={run} busy={busy} />
        <ArrearsPanel d={d} editable={c.status === 'preparing' && (owner1 || d.access.is_figures_checker || d.access.is_admin)} run={run} />
        <CashPanel d={d} editable={c.status === 'preparing'} run={run} />
        <Ui19Panel d={d} run={run} busy={busy} />
        <TermsPanel d={d} run={run} busy={busy} />
        {d.access.is_approver && <ApproverPanel d={d} run={run} />}
        <FiguresPanel d={d} run={run} busy={busy} />
        <Actions d={d} run={run} busy={busy} />
        <ReviewsPanel d={d} run={run} />
        <Events d={d} />
      </div>
    </div>
  )
}

/**
 * The one thing this person does next, with its button. Everything else on the
 * page is the record behind it. See yourTurn in utils/separation.ts for why.
 */
function YourTurn({ d, run, busy }: { d: Detail; run: Run; busy: string | null }) {
  const decide = useDecideSeparationStep<{ ok: boolean; next?: string }>()
  const issue = useIssueSeparation<{ ok: boolean }>()
  const t = yourTurn(d)
  const c = d.case
  const go = (id?: string) => id && document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  return (
    <div className={`space-y-2 rounded-lg p-3 ${t.mine ? 'border-2 border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/10' : 'border border-border bg-muted/40'}`}>
      <p className="text-sm font-semibold text-foreground">{t.title}</p>
      <p className="text-sm text-foreground">{t.detail}</p>
      {t.mine && (
        <div className="flex flex-wrap gap-2">
          {t.action === 'complete_step1' && (
            <Button size="sm" disabled={busy !== null} onClick={() => void run('Step 1 completed', () => decide.trigger({ id: c.id, step: 1, decision: 'approved', note: '', details: {} }).result)}>
              {busy === 'Step 1 completed' ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />} Complete step 1 and issue
            </Button>
          )}
          {t.action === 'issue' && (
            <Button size="sm" disabled={busy !== null} onClick={() => void run('Agreement issued', () => issue.trigger({ id: c.id }).result)}>
              {busy === 'Agreement issued' ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <FileText className="mr-1 h-3.5 w-3.5" />} Issue the agreement
            </Button>
          )}
          {['fix', 'ui19', 'step'].includes(t.action) && (
            <Button size="sm" onClick={() => go(t.goto)}><ArrowDown className="mr-1 h-3.5 w-3.5" /> {t.action === 'step' ? `Open step ${t.step}` : 'Show me'}</Button>
          )}
        </div>
      )}
      <ol className="flex flex-wrap gap-1 text-[11px] text-muted-foreground">
        {[
          ['Step 1', d.reviews.find((r) => r.step === 1)?.status === 'approved'],
          ['Issued', !!c.issued_at],
          ['Step 2', d.reviews.find((r) => r.step === 2)?.status === 'approved'],
          ['Step 3', d.reviews.find((r) => r.step === 3)?.status === 'approved'],
          ['Step 4', d.reviews.find((r) => r.step === 4)?.status === 'approved'],
          ['Sent to him', !!c.delivered_at],
          ['Signed', c.status === 'signed'],
        ].map(([label, done], i) => (
          <li key={i} className={`rounded px-1.5 py-0.5 ${done ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200' : 'bg-background'}`}>{done ? '\u2713 ' : ''}{label as string}</li>
        ))}
      </ol>
    </div>
  )
}

function Section({ id, title, children, right }: { id?: string; title: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-4 space-y-2 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  )
}

function DocumentButton({ id, status }: { id: string; status: string }) {
  const doc = useOpenSeparationDocument<{ ok: boolean; url?: string; error?: string }>()
  const [loading, setLoading] = useState(false)
  return (
    <Button size="sm" variant="outline" disabled={loading} onClick={async () => {
      setLoading(true)
      try {
        const r = await doc.trigger({ id }).result
        if (r.url) window.open(r.url, '_blank', 'noopener')
        else toast.error(r.error ?? 'The document could not be opened.')
      } catch (e) { toast.error(writeErrorMessage(e)) } finally { setLoading(false) }
    }}>
      {loading ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <FileText className="mr-1 h-3.5 w-3.5" />}
      {status === 'preparing' ? 'Draft PDF for checking' : status === 'signed' ? 'Signed agreement' : 'Agreement PDF'}
    </Button>
  )
}

function BasisCard({ d }: { d: Detail }) {
  const b = d.case.basis_detail as { reason?: string } | null
  return (
    <Section id="sec-basis" title="Contract basis">
      <p className="text-sm text-foreground">{b?.reason ?? d.basis_live.reason}</p>
      <p className="text-xs text-muted-foreground">
        {d.case.basis ? `Frozen when the agreement was issued on ${formatWhen(d.case.issued_at)}.` : 'Read live until the agreement is issued, then frozen.'}
        {' '}Every clause the agreement cites comes from this contract's map, not from the newest agreement.
      </p>
      {d.clause_map && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">Clause map ({d.clause_map.filter((m) => m.applies).length} rules apply)</summary>
          <ul className="mt-1 space-y-0.5">
            {d.clause_map.map((m) => (
              <li key={m.rule}><span className="font-medium">{m.rule.replace(/_/g, ' ')}</span>: {m.applies ? `clause ${m.clause}` : 'not in this contract'}{m.note ? `. ${m.note}` : ''}</li>
            ))}
          </ul>
        </details>
      )}
    </Section>
  )
}

function Step1Form({ d, editable, run, busy }: { d: Detail; editable: boolean; run: Run; busy: string | null }) {
  const save = useUpdateSeparation<{ ok: boolean }>()
  const initial = useMemo(() => formFrom(d.case), [d.case])
  const [f, setF] = useState(initial)
  const [link, setLink] = useState({ conduct_case_id: d.case.conduct_case_id ?? '', disciplinary_incident_id: d.case.disciplinary_incident_id ?? '' })
  useEffect(() => { setF(initial) }, [initial])
  const patch = { ...patchFrom(initial, f) }
  if ((link.conduct_case_id || null) !== (d.case.conduct_case_id ?? null)) patch.conduct_case_id = link.conduct_case_id || null
  if ((link.disciplinary_incident_id || null) !== (d.case.disciplinary_incident_id ?? null)) patch.disciplinary_incident_id = link.disciplinary_incident_id || null
  const dirty = Object.keys(patch).length > 0
  const s1 = d.reviews.find((r) => r.step === 1)
  const set = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }))

  const field = (fd: (typeof STEP1_FIELDS)[number]) => (
    <div key={fd.key} className={fd.kind === 'long' ? 'space-y-1 sm:col-span-2' : 'space-y-1'}>
      <Label className="text-xs">{fd.label}</Label>
      {fd.kind === 'long' ? (
        <Textarea className="min-h-[60px] text-xs" disabled={!editable} value={f[fd.key]} onChange={(e) => set(fd.key, e.target.value)} />
      ) : fd.kind === 'bool' ? (
        <NativeSelect className="h-8 text-xs" disabled={!editable} value={f[fd.key]} onChange={(e) => set(fd.key, e.target.value)}>
          <option value="">Not answered</option><option value="yes">Yes</option><option value="no">No</option>
        </NativeSelect>
      ) : fd.kind === 'choice' ? (
        <NativeSelect className="h-8 text-xs" disabled={!editable} value={f[fd.key]} onChange={(e) => set(fd.key, e.target.value)}>
          <option value="">Not answered</option>
          {fd.choices!.map((ch) => <option key={ch.value} value={ch.value}>{ch.label}</option>)}
        </NativeSelect>
      ) : (
        <Input className="h-8 text-xs" disabled={!editable} type={fd.kind === 'date' ? 'date' : fd.kind === 'text' ? 'text' : 'number'}
          value={f[fd.key]} onChange={(e) => set(fd.key, e.target.value)} />
      )}
      {fd.hint && <p className="text-[11px] text-muted-foreground">{fd.hint}</p>}
    </div>
  )

  return (
    <Section id="sec-step1" title={`Step 1: the request and the terms${s1?.assignee ? `, ${s1.assignee}` : ''}`}
      right={!editable ? <span className="text-[11px] text-muted-foreground">{d.case.status === 'preparing' ? `Only ${s1?.assignee ?? 'the Driver Servant'} or an admin edits this.` : 'Frozen since issue.'}</span> : null}>
      <div className="space-y-3">
        {STEP1_GROUPS.map((g) => {
          const fields = STEP1_FIELDS.filter((x) => x.group === g.key)
          const answered = fields.filter((x) => f[x.key] !== '').length
          return (
            <div key={g.key} id={`sec-${g.key}`} className="scroll-mt-4 space-y-2 rounded-md bg-muted/30 p-2.5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h4 className="text-xs font-semibold text-foreground">{g.title}</h4>
                <span className="text-[11px] text-muted-foreground">{answered} of {fields.length} answered</span>
              </div>
              <p className="text-[11px] text-muted-foreground">{g.intro}</p>
              {g.key === 'allegation' && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Conduct case for the allegation</Label>
                    <NativeSelect className="h-8 text-xs" disabled={!editable} value={link.conduct_case_id} onChange={(e) => setLink((x) => ({ ...x, conduct_case_id: e.target.value }))}>
                      <option value="">None linked</option>
                      {d.conduct_options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </NativeSelect>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Or the disciplinary incident</Label>
                    <NativeSelect className="h-8 text-xs" disabled={!editable} value={link.disciplinary_incident_id} onChange={(e) => setLink((x) => ({ ...x, disciplinary_incident_id: e.target.value }))}>
                      <option value="">None linked</option>
                      {d.incident_options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </NativeSelect>
                  </div>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">{fields.map(field)}</div>
              {g.key === 'who' && raisedInAllegationMeeting(f) && (
                <p className="flex gap-1.5 rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  With these answers the agreement cannot be issued: Fleetly suggesting separation in the meeting where the allegation was first put is not a free choice for the driver. If that is what happened, withdraw the separation and continue to the hearing. If not, correct the answers.
                </p>
              )}
            </div>
          )
        })}
      </div>
      {editable && (
        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-border bg-background/95 py-2 backdrop-blur">
          {dirty && <span className="text-[11px] text-amber-700 dark:text-amber-300">Unsaved changes</span>}
          <Button size="sm" disabled={!dirty || busy !== null} onClick={() => void run('Step 1 saved', () => save.trigger({ id: d.case.id, patch }).result, true)}>Save step 1</Button>
        </div>
      )}
    </Section>
  )
}

/** Everything still standing between this case and the next stage, each with a way to fix it. */
function WhatIsLeft({ d, run, busy }: { d: Detail; run: Run; busy: string | null }) {
  const compute = useComputeSeparation<{ ok: boolean }>()
  const c = d.case
  if (!['preparing', 'issued'].includes(c.status)) return null
  const gates = c.status === 'preparing' ? d.issue_gates : d.sign_gates
  const bad = failing(gates)
  const stale = c.status === 'preparing' && d.figures_computed_at && c.updated_at && new Date(d.figures_computed_at).getTime() < new Date(c.updated_at).getTime() - 2000
  const go = (key: string) => document.getElementById(GATE_SECTION[key] ?? 'sec-step1')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  return (
    <section className="space-y-2 rounded-lg border-2 border-[hsl(var(--brand))]/40 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">What is left {c.status === 'preparing' ? 'before it can be issued' : 'before signature'}</h3>
        <Chip tone={bad.length ? 'amber' : 'green'}>{bad.length ? `${bad.length} to settle` : 'Nothing left'}</Chip>
      </div>
      {stale && <p className="text-xs text-amber-700 dark:text-amber-300">Something changed after the figures were computed. Press Compute figures again before step 1.</p>}
      {bad.length === 0 && <p className="text-xs text-muted-foreground">Every check passes.</p>}
      <ul className="space-y-1.5">
        {bad.map((g) => (
          <li key={g.key} className="flex items-start gap-2 text-xs">
            <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-600" />
            <span className="flex-1 text-foreground">
              {g.text}
              {g.key === 'figures' && d.figures_computed_at && <span className="text-muted-foreground"> (as computed {formatWhen(d.figures_computed_at)})</span>}
            </span>
            {g.key === 'figures' && c.status === 'preparing' && (
              <button type="button" disabled={busy !== null} onClick={() => void run('Figures computed', () => compute.trigger({ id: c.id }).result)}
                className="shrink-0 whitespace-nowrap text-[11px] font-medium text-[hsl(var(--brand))] hover:underline disabled:opacity-50">
                {busy === 'Figures computed' ? 'Computing…' : 'Compute now'}
              </button>
            )}
            <button type="button" onClick={() => go(g.key)} className="flex shrink-0 items-center gap-0.5 whitespace-nowrap text-[11px] font-medium text-[hsl(var(--brand))] hover:underline">
              Go there <ArrowDown className="h-3 w-3" />
            </button>
          </li>
        ))}
      </ul>
      {gates && gates.some((g) => g.ok) && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">{gates.filter((g) => g.ok).length} already settled</summary>
          <ul className="mt-1 space-y-0.5">
            {gates.filter((g) => g.ok).map((g) => (
              <li key={g.key} className="flex gap-2 text-muted-foreground"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />{g.text}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

/** The exit inspection and the leave balance, both read from where they are recorded. */
function InspectionLeavePanel({ d, editable, run, busy }: { d: Detail; editable: boolean; run: Run; busy: string | null }) {
  const attach = useAttachSeparationInspection<{ ok: boolean; note?: string }>()
  const leave = useReadSeparationLeave<{ ok: boolean; days: number }>()
  const i = d.inspection
  const l = d.leave
  return (
    <Section id="sec-inspection" title="Exit inspection and leave">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5 rounded-md bg-muted/30 p-2.5 text-xs">
          <h4 className="font-semibold text-foreground">Exit inspection</h4>
          {!i && <p className="text-muted-foreground">No Part C inspection on file for him. Do the handover inspection with him present (or two Fleetly representatives, sent to him the same day), then attach it here.</p>}
          {i && (
            <p className="text-muted-foreground">
              {i.attached ? 'Attached: ' : 'His last Part C: '}{formatWhen(i.inspected_at)}, {i.registration ?? 'car not named'}, {i.odometer ?? 'no'} km, fuel {i.fuel ?? 'not recorded'}.
              {!i.attached && !i.in_window && ' It is more than two days older than his last working day, so a fresh handover inspection is needed.'}
            </p>
          )}
          {editable && i && !i.attached && (
            <Button size="sm" variant="outline" disabled={busy !== null || !i.in_window}
              onClick={() => void run('Exit inspection attached', async () => {
                const r = await attach.trigger({ id: d.case.id }).result
                if (r.note) toast.message(r.note)
                return r
              }, true)}>Attach his last Part C</Button>
          )}
        </div>
        <div className="space-y-1.5 rounded-md bg-muted/30 p-2.5 text-xs">
          <h4 className="font-semibold text-foreground">Annual leave</h4>
          <p className="text-muted-foreground">
            {l?.current ? `${Number(l.days).toFixed(2)} days on ${formatDay(l.for_date)}, read from SimplePay ${formatWhen(l.read_at)} by ${l.by}.`
              : l?.for_date ? `Read for ${formatDay(l.for_date)}, but the Termination Date is now ${formatDay(d.case.termination_date)}. Read it again.`
              : `Not read yet. It is read from SimplePay for the Termination Date${d.case.termination_date ? `, ${formatDay(d.case.termination_date)}` : ''}, never typed.`}
          </p>
          {editable && (
            <Button size="sm" variant="outline" disabled={busy !== null || !d.case.termination_date}
              onClick={() => void run('Leave read from SimplePay', () => leave.trigger({ id: d.case.id }).result, true)}>Read leave from SimplePay</Button>
          )}
        </div>
      </div>
    </Section>
  )
}

function ArrearsPanel({ d, editable, run }: { d: Detail; editable: boolean; run: Run }) {
  const rec = useRecordSeparationArrears<{ ok: boolean }>()
  const pp = d.pay_periods_to_check
  const [item, setItem] = useState({ pay_period: '', component: 'guaranteed', amount_zar: '', source: '' })
  const [source, setSource] = useState(d.case.arrears_check_source ?? '')
  const [adding, setAdding] = useState(false)
  const live = d.arrears_items.filter((a) => !a.withdrawn)
  return (
    <Section id="sec-arrears" title="Arrears: pay still owed from earlier pay periods">
      <p className="text-xs text-muted-foreground">
        This line is never waived and never blank. Compare what he was owed with what SimplePay paid for every earlier pay period
        {pp?.from && pp?.to ? `, ${formatDay(pp.from)} to ${formatDay(pp.to)}` : ''}, record any amount still owed, then record the check. Nothing owed is a checked nil.
      </p>
      <p className="text-xs text-foreground">
        {d.case.arrears_checked_by
          ? `Checked ${formatDay(d.case.arrears_checked_from)} to ${formatDay(d.case.arrears_checked_to)} by ${d.people.arrears_checked_by}, against ${d.case.arrears_check_source}. ${live.length ? `${live.length} amount${live.length > 1 ? 's' : ''} owed recorded.` : 'Nothing owed.'}`
          : 'Not checked yet.'}
      </p>
      {live.length > 0 && (
        <ul className="space-y-1 text-xs">
          {live.map((a) => (
            <li key={a.id}>
              {a.pay_period}, {a.component.replace('_', ' ')}: {formatZar(a.amount_zar)} ({a.source}), recorded by {a.recorded_by}
              {editable && <button type="button" className="ml-2 underline" onClick={() => void run('Arrears amount withdrawn', () => rec.trigger({ id: d.case.id, action: 'withdraw', payload: { item_id: a.id, note: 'Withdrawn on the page' } }).result, true)}>withdraw</button>}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Input className="h-8 min-w-[16rem] flex-1 text-xs" placeholder="Checked against, for example: SimplePay payslips and the platform pay records" value={source} onChange={(e) => setSource(e.target.value)} />
            <Button size="sm" variant="outline" disabled={!pp?.from || !pp?.to}
              onClick={() => void run('Arrears check recorded', () => rec.trigger({ id: d.case.id, action: 'checked', payload: { from: pp!.from, to: pp!.to, source } }).result, true)}>
              Record the check{pp?.from && pp?.to ? `, ${formatDay(pp.from)} to ${formatDay(pp.to)}` : ''}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding((x) => !x)}>{adding ? 'Cancel' : 'Add an amount owed'}</Button>
          </div>
          {adding && (
            <div className="grid gap-2 sm:grid-cols-5">
              <Input className="h-8 text-xs" placeholder="Pay period, e.g. 25 Aug to 24 Sep 2026" value={item.pay_period} onChange={(e) => setItem({ ...item, pay_period: e.target.value })} />
              <NativeSelect className="h-8 text-xs" value={item.component} onChange={(e) => setItem({ ...item, component: e.target.value })}>
                <option value="guaranteed">Guaranteed Base Salary</option><option value="revenue_share">Revenue Share</option><option value="other">Other remuneration</option>
              </NativeSelect>
              <Input className="h-8 text-xs" type="number" placeholder="Amount owed (R)" value={item.amount_zar} onChange={(e) => setItem({ ...item, amount_zar: e.target.value })} />
              <Input className="h-8 text-xs" placeholder="Where it shows" value={item.source} onChange={(e) => setItem({ ...item, source: e.target.value })} />
              <Button size="sm" variant="outline" onClick={() => void run('Amount owed recorded', async () => {
                const r = await rec.trigger({ id: d.case.id, action: 'add', payload: { ...item, amount_zar: Number(item.amount_zar) } }).result
                setAdding(false); setItem({ pay_period: '', component: 'guaranteed', amount_zar: '', source: '' })
                return r
              }, true)}>Add</Button>
            </div>
          )}
        </div>
      )}
    </Section>
  )
}

function CashPanel({ d, editable, run }: { d: Detail; editable: boolean; run: Run }) {
  const cash = useRecordSeparationCash<{ ok: boolean }>()
  // Only v6 and the addendum have clause 14A; v5 and a contract still on manual review get the neutral wording.
  const v5 = ['v5', 'manual_review'].includes(d.case.basis ?? d.basis_live.basis)
  const [f, setF] = useState({ amount_zar: d.case.cash_amount_zar ?? '', note: d.case.cash_note ?? '', delivered_on: d.case.cash_statement_delivered_on ?? '', disputed: d.case.cash_statement_disputed ? 'yes' : 'no' })
  const [dec, setDec] = useState({ decision: '', amount_zar: '' })
  return (
    <Section id="sec-cash" title={v5 ? 'Cash position' : 'Final cash statement (clause 14A)'}>
      <p className="text-xs text-muted-foreground">
        {d.case.cash_recorded_by
          ? `Recorded by ${d.people.cash_recorded_by}: ${formatZar(d.case.cash_amount_zar)}${d.case.cash_statement_delivered_on ? `, given to the driver ${formatDay(d.case.cash_statement_delivered_on)}` : ''}${d.case.cash_statement_disputed ? (d.case.cash_dispute_decided_on ? `, dispute decided ${formatDay(d.case.cash_dispute_decided_on)}: ${d.case.cash_dispute_decision}` : ', disputed and not yet decided') : ''}.`
          : 'Not recorded. Nil is a figure; record it.'}
        {' '}Enter 0 when he owes nothing. A balance is listed in Annexure A only once the statement is final: three working days without a dispute, or a dispute decided in writing by a manager who is not the driver's Driver Servant.
      </p>
      {editable && (
        <div className="grid items-end gap-2 sm:grid-cols-5">
          <div className="space-y-1"><Label className="text-xs">Cash owed to Fleetly (R)</Label>
            <Input className="h-8 text-xs" type="number" value={f.amount_zar} onChange={(e) => setF({ ...f, amount_zar: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Statement given on</Label>
            <Input className="h-8 text-xs" type="date" value={f.delivered_on} onChange={(e) => setF({ ...f, delivered_on: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Disputed?</Label>
            <NativeSelect className="h-8 text-xs" value={f.disputed} onChange={(e) => setF({ ...f, disputed: e.target.value })}>
              <option value="no">Not disputed</option><option value="yes">Disputed</option>
            </NativeSelect></div>
          <div className="space-y-1"><Label className="text-xs">Source</Label>
            <Input className="h-8 text-xs" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
          <Button size="sm" variant="outline" onClick={() => void run('Cash recorded', () => cash.trigger({ id: d.case.id, action: 'record', payload: { ...f, amount_zar: f.amount_zar === '' ? null : Number(f.amount_zar), disputed: f.disputed === 'yes' } }).result, true)}>Record</Button>
          {d.case.cash_statement_disputed && !d.case.cash_dispute_decided_on && (
            <>
              <Input className="h-8 text-xs sm:col-span-3" placeholder="The decision, in writing" value={dec.decision} onChange={(e) => setDec({ ...dec, decision: e.target.value })} />
              <Input className="h-8 text-xs" type="number" placeholder="Amount that stands (R)" value={dec.amount_zar} onChange={(e) => setDec({ ...dec, amount_zar: e.target.value })} />
              <Button size="sm" variant="outline" onClick={() => void run('Dispute decided', () => cash.trigger({ id: d.case.id, action: 'decide_dispute', payload: { decision: dec.decision, amount_zar: Number(dec.amount_zar) } }).result)}>Decide</Button>
            </>
          )}
        </div>
      )}
    </Section>
  )
}

/**
 * Luyanda's decisions on the terms of this one case (5416): a backdated
 * Termination Date with no pay after the last shift, the damage, and deducting
 * it in full past the 25 percent cap. Each is printed in the agreement (2.1A,
 * A.2F) so the driver signs knowing it. Everyone sees them; only he records them.
 */
function TermsPanel({ d, run, busy }: { d: Detail; run: Run; busy: string | null }) {
  const act = useDecideSeparationApprover<{ ok: boolean }>()
  const c = d.case
  const can = d.access.is_approver && c.status === 'preparing'
  const backdated = !!c.backdated_at && c.backdated_to === c.termination_date
  const [b, setB] = useState({ termination_date: c.backdated_to ?? '', reason: c.backdated_reason ?? '' })
  const [fd, setFd] = useState(c.full_deduction_reason ?? '')
  const [dm, setDm] = useState({ amount: c.damage_amount_zar != null ? String(c.damage_amount_zar) : '', description: c.damage_description ?? '', source: c.damage_source ?? '', notice_on: c.damage_notice_on ?? '', balance: c.damage_balance ?? 'waived' })
  const go = (label: string, action: string, payload: Record<string, unknown> = {}) => void run(label, () => act.trigger({ id: c.id, action, payload }).result, true)
  const ann = fullFigures(d.figures) ? (d.figures.annexure_a as Record<string, any>) : null
  if (!can && !c.backdated_at && !c.full_deduction_at && c.damage_amount_zar == null) return null
  return (
    <Section id="sec-terms-decisions" title="Luyanda's decisions on the terms">
      <p className="text-[11px] text-muted-foreground">Each decision is printed in the agreement in plain words, so the driver signs knowing it. Changing one reopens step 1.</p>

      <div className="space-y-1.5 rounded-md bg-muted/30 p-2.5">
        <h4 className="text-xs font-semibold text-foreground">Termination Date in the past, no pay after the last shift</h4>
        <p className="text-xs text-foreground">
          {backdated ? `Backdated to ${formatDay(c.termination_date)} by ${d.people.backdated_by ?? 'the approver'}: ${c.backdated_reason}. No pay after the last shift on ${formatDay(d.offboarding.last_working_day)}, suspension included (clause 2.1A).`
            : `Not backdated. Last shift ${formatDay(d.offboarding.last_working_day)}${c.suspended_on ? `, suspended ${formatDay(c.suspended_on)}` : ''}; pay runs to the Termination Date.`}
        </p>
        {can && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1"><Label className="text-xs">Termination Date</Label>
              <Input className="h-8 text-xs" type="date" value={b.termination_date} onChange={(e) => setB({ ...b, termination_date: e.target.value })} /></div>
            <div className="min-w-[14rem] flex-1 space-y-1"><Label className="text-xs">Reason</Label>
              <Input className="h-8 text-xs" placeholder="Why he is not paid after his last shift" value={b.reason} onChange={(e) => setB({ ...b, reason: e.target.value })} /></div>
            <Button size="sm" disabled={busy !== null} onClick={() => go('Termination Date backdated', 'backdate', b)}>Backdate</Button>
            {c.backdated_at && <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => go('Backdating removed', 'backdate_off')}>Undo</Button>}
          </div>
        )}
        {can && backdated && <p className="text-[11px] text-amber-700 dark:text-amber-300">Leave is read again for the new date: press "Read leave" under Exit inspection and leave.</p>}
      </div>

      <div className="space-y-1.5 rounded-md bg-muted/30 p-2.5">
        <h4 className="text-xs font-semibold text-foreground">Damage to the car</h4>
        <p className="text-xs text-foreground">
          {c.damage_amount_zar != null
            ? c.damage_balance === 'waived'
              ? `${c.damage_description}: ${formatZar(Number(c.damage_amount_zar))}, from ${c.damage_source}. Given up in exchange for his suspension pay: shown in clause 2.1B, never deducted, never pursued.`
              : `${c.damage_description}: ${formatZar(Number(c.damage_amount_zar))}, from ${c.damage_source}; written notice given ${formatDay(c.damage_notice_on)}. Any balance the final pay cannot carry ${c.damage_balance === 'written_off' ? 'is written off' : 'stays owed by him'}.`
            : 'No damage recorded on this separation.'}
          {ann && c.damage_amount_zar != null && c.damage_balance !== 'waived' && ` Deducted: ${formatZar(Number(ann.items?.find((x: any) => x.kind === 'damage')?.allowed ?? 0))}; balance ${formatZar(Number(ann.damage_balance ?? 0))}.`}
        </p>
        {can && (
          <div className="grid gap-2 sm:grid-cols-2">
            <Input className="h-8 text-xs" type="number" placeholder="Amount (R)" value={dm.amount} onChange={(e) => setDm({ ...dm, amount: e.target.value })} />
            <Input className="h-8 text-xs" placeholder="What was damaged" value={dm.description} onChange={(e) => setDm({ ...dm, description: e.target.value })} />
            <Input className="h-8 text-xs" placeholder="Quotation or invoice (supplier and number)" value={dm.source} onChange={(e) => setDm({ ...dm, source: e.target.value })} />
            <div className="space-y-1"><Label className="text-xs">Written notice with the quotation given to him on{dm.balance === 'waived' ? ' (not needed for the exchange)' : ''}</Label>
              <Input className="h-8 text-xs" type="date" value={dm.notice_on} onChange={(e) => setDm({ ...dm, notice_on: e.target.value })} /></div>
            <NativeSelect className="h-8 text-xs" value={dm.balance} onChange={(e) => setDm({ ...dm, balance: e.target.value })}>
              <option value="waived">Given up in exchange for his suspension pay (clause 2.1B), not deducted</option>
              <option value="owed">A balance the final pay cannot carry stays owed by him</option>
              <option value="written_off">A balance the final pay cannot carry is written off</option>
            </NativeSelect>
            <div className="flex gap-2">
              <Button size="sm" disabled={busy !== null} onClick={() => go('Damage recorded', 'damage', { ...dm, amount: Number(dm.amount) })}>Record the damage</Button>
              {c.damage_amount_zar != null && <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => go('Damage removed', 'damage_off')}>Remove</Button>}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-1.5 rounded-md bg-muted/30 p-2.5">
        <h4 className="text-xs font-semibold text-foreground">Deduct in full</h4>
        <p className="text-xs text-foreground">
          {c.full_deduction_at
            ? `Deducted in full by ${d.people.full_deduction_by ?? 'the approver'}: ${c.full_deduction_reason}. No 25 percent cap and no minimum wage floor; UIF, PAYE and medical still come first, and the net never goes below nothing (Annexure A, A.2F).`
            : 'Not decided: deductions stop at 25 percent of his pay and never take him below the minimum wage.'}
        </p>
        {can && (
          <div className="flex flex-wrap items-end gap-2">
            <Input className="h-8 min-w-[14rem] flex-1 text-xs" placeholder="Reason" value={fd} onChange={(e) => setFd(e.target.value)} />
            <Button size="sm" disabled={busy !== null} onClick={() => go('Full deduction recorded', 'full_deduction', { reason: fd })}>Deduct in full</Button>
            {c.full_deduction_at && <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => go('Full deduction removed', 'full_deduction_off')}>Undo</Button>}
          </div>
        )}
      </div>
    </Section>
  )
}

function ApproverPanel({ d, run }: { d: Detail; run: Run }) {
  const act = useDecideSeparationApprover<{ ok: boolean }>()
  const [note, setNote] = useState('')
  const [board, setBoard] = useState(d.case.board_approval_ref ?? '')
  const esc = fullFigures(d.figures) ? d.figures.escalate : {}
  const flags = Object.entries(esc).filter(([, v]) => v).map(([k]) => k.replace(/_/g, ' '))
  return (
    <Section id="sec-approver" title="Escalation and the board">
      <div className="grid gap-2 sm:grid-cols-4">
        <Input className="h-8 text-xs sm:col-span-3" placeholder={flags.length ? `Escalated: ${flags.join(', ')}. What you considered.` : 'Nothing is escalated.'} value={note} onChange={(e) => setNote(e.target.value)} />
        <Button size="sm" variant="outline" disabled={!flags.length} onClick={() => void run('Escalation considered', () => act.trigger({ id: d.case.id, action: 'escalation', payload: { note } }).result)}>Record</Button>
        <Input className="h-8 text-xs sm:col-span-3" placeholder="Board approval reference, where above the Delegation of Authority" value={board} onChange={(e) => setBoard(e.target.value)} />
        <Button size="sm" variant="outline" onClick={() => void run('Board approval recorded', () => act.trigger({ id: d.case.id, action: 'board', payload: { ref: board } }).result)}>Record</Button>
      </div>
      {d.case.escalation_ack_by && <p className="text-[11px] text-muted-foreground">Escalation considered by {d.people.escalation_ack_by}: {d.case.escalation_ack_note}</p>}
    </Section>
  )
}

/**
 * The UI-19 reason: everyone sees it, Luyanda decides the code, and whoever
 * tells the driver (usually the Driver Servant) records when and how. On 7 Oct
 * 2026 Thabang's "Go there" went nowhere because this was drawn for the
 * approver only.
 */
function Ui19Panel({ d, run, busy }: { d: Detail; run: Run; busy: string | null }) {
  const decide = useDecideSeparationApprover<{ ok: boolean }>()
  const told = useRecordSeparationUi19Told<{ ok: boolean }>()
  const c = d.case
  const sug = d.ui19_suggestion
  const preparing = c.status === 'preparing'
  const canTell = preparing && (d.reviews.find((r) => r.step === 1)?.assignee_id === d.me || d.access.is_approver || d.access.is_admin)
  const [code, setCode] = useState(String(c.ui19_code ?? (sug?.certain && sug.code ? sug.code : '')))
  const [t, setT] = useState({ told_on: c.ui19_told_on ?? '', method: c.ui19_told_method ?? '' })
  const label = (n: number | null | undefined) => (n === 16 ? 'Voluntary severance package (16)' : n === 6 ? 'Resigned (6)' : 'not decided')
  return (
    <Section id="sec-ui19" title="UI-19 reason">
      {sug && (
        <p className={`rounded-md border p-2 text-xs ${sug.certain ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200' : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'}`}>
          {sug.certain ? `From the facts of this case: code ${sug.code}. ` : 'From the facts of this case: no code is plainly true yet. '}{sug.reason}
        </p>
      )}
      <p className="text-xs text-foreground">
        {c.ui19_code ? `Decided: ${label(c.ui19_code)}, by ${d.people.ui19_decided_by}.` : 'Luyanda has not decided the reason yet.'}{' '}
        {c.ui19_told_on ? `He was told on ${formatDay(c.ui19_told_on)} (${c.ui19_told_method}).` : c.ui19_code ? 'He has not been told yet.' : ''}
      </p>
      {preparing && d.access.is_approver && (
        <div className="flex flex-wrap items-center gap-2">
          <NativeSelect className="h-8 w-auto text-xs" value={code} onChange={(e) => setCode(e.target.value)}>
            <option value="">Choose the reason</option><option value="16">Voluntary severance package (16)</option><option value="6">Resigned (6)</option>
          </NativeSelect>
          <Button size="sm" variant="outline" disabled={busy !== null || !code}
            onClick={() => void run('UI-19 reason recorded', () => decide.trigger({ id: c.id, action: 'ui19', payload: { code: Number(code), told_on: c.ui19_told_on, told_method: c.ui19_told_method } }).result)}>
            Record the reason
          </Button>
        </div>
      )}
      {canTell && c.ui19_code && (
        <div className="space-y-1">
          <p className="text-[11px] text-muted-foreground">Tell him the reason that will be recorded, {label(c.ui19_code)}, and that UIF decides whether to pay on it. Then record when and how.</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1"><Label className="text-xs">Told on</Label>
              <Input className="h-8 text-xs" type="date" value={t.told_on} onChange={(e) => setT({ ...t, told_on: e.target.value })} /></div>
            <div className="min-w-[12rem] flex-1 space-y-1"><Label className="text-xs">How</Label>
              <Input className="h-8 text-xs" placeholder="In person, phone call or WhatsApp" value={t.method} onChange={(e) => setT({ ...t, method: e.target.value })} /></div>
            <Button size="sm" variant="outline" disabled={busy !== null}
              onClick={() => void run('Recorded that he was told', () => told.trigger({ id: c.id, told_on: t.told_on, method: t.method }).result)}>Record that he was told</Button>
          </div>
        </div>
      )}
      {preparing && !c.ui19_code && !d.access.is_approver && <p className="text-[11px] text-muted-foreground">Once Luyanda records the reason, you record here when and how you told him.</p>}
    </Section>
  )
}

function FiguresPanel({ d, run, busy }: { d: Detail; run: Run; busy: string | null }) {
  const compute = useComputeSeparation<{ ok: boolean }>()
  const check = useCheckSeparationSources<{ ok: boolean; changed: boolean }>()
  const f = d.figures
  const preparing = d.case.status === 'preparing'
  return (
    <Section id="sec-figures" title={preparing ? 'Figures, as last computed' : 'Figures, frozen at issue'}
      right={preparing ? (
        <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void run('Figures computed', () => compute.trigger({ id: d.case.id }).result)}>
          {busy === 'Figures computed' ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />} Compute figures
        </Button>
      ) : ['issued', 'signing'].includes(d.case.status) ? (
        <Button size="sm" variant="outline" disabled={busy !== null} onClick={async () => {
          try {
            const r = await check.trigger({ id: d.case.id }).result
            if (r.changed) toast.error('A source record has changed since issue. Return the agreement to preparing; it must be delivered again.')
            else toast.success('No source record has changed since issue.')
          } catch (e) { toast.error(writeErrorMessage(e)) }
        }}>Check the sources now</Button>
      ) : null}>
      {!f && <p className="text-xs text-muted-foreground">Not computed yet. It takes a few seconds: thirteen weeks of payroll are read.</p>}
      {f && !fullFigures(f) && (
        <div className="space-y-1 rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">
          <p className="font-medium">No figures, computed {formatWhen(d.figures_computed_at)}. Nothing missing is ever shown as zero.</p>
          <ul className="list-disc pl-4">{(f.missing ?? []).map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
      {f && fullFigures(f) && (
        <>
          <p className="text-xs text-muted-foreground">
            Computed {formatWhen(d.figures_computed_at)} · pay period from {formatDay(f.period_start)} · pay by {formatDay(f.pay_by)} ·
            average {formatZar(f.averages.weekly)} a week over {f.averages.weeks} weeks to {formatDay(f.averages.to)}
          </p>
          {f.status === 'blocked' && (
            <div className="space-y-1 rounded-md border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">
              <p className="font-medium">These figures cannot be computed yet. Nothing missing is ever shown as zero.</p>
              <ul className="list-disc pl-4">{f.missing.map((m) => <li key={m}>{m}</li>)}</ul>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-2">Line</th><th className="pr-2">Period</th><th className="pr-2">Basis and source</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {f.lines.map((l) => (
                  <tr key={l.key} className="border-t border-border align-top">
                    <td className="py-1 pr-2 font-medium">{LINE_LABEL[l.key] ?? l.key}{l.kind === 'deduction' ? ' (deduction)' : ''}</td>
                    <td className="pr-2">{l.period}</td>
                    <td className="pr-2 text-muted-foreground">{l.basis} <span className="italic">Source: {l.source}</span></td>
                    <td className="whitespace-nowrap text-right">{lineAmount(l)}</td>
                  </tr>
                ))}
                <tr className="border-t border-border font-medium"><td className="py-1" colSpan={3}>Annexure A deductions, after the cap and the floor</td><td className="text-right">{formatZar(f.annexure_a.total)}</td></tr>
                <tr className="border-t border-border font-semibold"><td className="py-1" colSpan={3}>Gross {formatZar(f.totals.earnings)}, deductions {formatZar(f.totals.deductions)}, net estimate</td><td className="text-right">{formatZar(f.totals.net_estimate)}</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            Cap: 25 percent of {formatZar(f.totals.remuneration_in_money)} is {formatZar(f.annexure_a.cap)}; ahead of Annexure A {formatZar(f.annexure_a.ranked_ahead)};
            floor {formatZar(f.annexure_a.floor)} for {f.totals.paid_hours} paid hours; room for Annexure A {formatZar(f.annexure_a.room)}.
          </p>
          {f.annexure_a.items.length > 0 && (
            <ul className="space-y-1 text-xs">
              {f.annexure_a.items.map((a, i) => (
                <li key={i}><span className="font-medium">{a.description}</span>: {formatZar(a.allowed)}{a.held_back > 0 ? ` (held back ${formatZar(a.held_back)} by the cap or floor)` : ''}. {a.source}{a.calculation ? ` ${a.calculation.join(' ')}` : ''}</li>
              ))}
            </ul>
          )}
          {f.excluded.length > 0 && (
            <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">{f.excluded.length} balances not deducted, with the reason</summary>
              <ul className="mt-1 space-y-0.5">{f.excluded.map((x, i) => <li key={i}>{x.source}: {formatZar(x.amount)}. {x.reason}</li>)}</ul>
            </details>
          )}
          {(f.info ?? []).map((m) => <p key={m} className="text-xs text-amber-700 dark:text-amber-300">{m}</p>)}
        </>
      )}
    </Section>
  )
}

function Actions({ d, run, busy }: { d: Detail; run: Run; busy: string | null }) {
  const issue = useIssueSeparation<{ ok: boolean }>()
  const deliver = useDeliverSeparation<{ ok: boolean }>()
  const reopen = useReopenSeparation<{ ok: boolean }>()
  const send = useSendSeparationForSignature<{ ok: boolean; error?: string }>()
  const sync = useSyncSeparationSignatures<{ ok: boolean }>()
  const close = useCloseSeparation<{ ok: boolean; next?: string }>()
  const [why, setWhy] = useState('')
  const c = d.case
  const owner1 = d.reviews.find((r) => r.step === 1)?.assignee_id === d.me || d.access.is_admin
  const open = ['preparing', 'issued'].includes(c.status)
  if (!open && c.status !== 'signing') return null
  return (
    <Section title="Actions">
      <div className="flex flex-wrap gap-2">
        {c.status === 'preparing' && owner1 && (
          <Button size="sm" disabled={busy !== null} onClick={() => void run('Agreement issued', () => issue.trigger({ id: c.id }).result)}>Issue the agreement</Button>
        )}
        {c.status === 'issued' && !c.delivered_at && owner1 && (
          <Button size="sm" disabled={busy !== null} onClick={() => void run('Delivery stamped', () => deliver.trigger({ id: c.id, method: 'in_person' }).result)}>I also handed him a printed copy</Button>
        )}
        {c.status === 'issued' && (d.access.is_approver || owner1) && (
          <Button size="sm" disabled={busy !== null || failing(d.sign_gates).length > 0} onClick={() => void run('Sent for signature', async () => {
            const r = await send.trigger({ id: c.id }).result
            if (!r.ok) throw new Error(r.error ?? 'Not sent.')
            return r
          })}><Send className="mr-1 h-3.5 w-3.5" /> Send for signature</Button>
        )}
        {c.status === 'signing' && (
          <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void run('Signatures checked', () => sync.trigger({ id: c.id }).result)}>Check signatures</Button>
        )}
      </div>
      {open && (
        <div className="grid gap-2 sm:grid-cols-4">
          <Input className="h-8 text-xs sm:col-span-2" placeholder="Why (for return, decline or withdraw)" value={why} onChange={(e) => setWhy(e.target.value)} />
          {c.status === 'issued' && owner1 && (
            <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void run('Returned to preparing', () => reopen.trigger({ id: c.id, why }).result)}>Return to preparing</Button>
          )}
          <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void run('Recorded as declined', () => close.trigger({ id: c.id, kind: 'declined', note: why }).result)}>The driver declined</Button>
          <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void run('Withdrawn', () => close.trigger({ id: c.id, kind: 'cancelled', note: why }).result)}>Withdraw</Button>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        Approving step 4 sends the agreement to the driver through SignNow, and that is the delivery. A printed copy is optional: if you record one, he may only sign two working days after it.
        Returning it to preparing, or any change to a figure, means delivering it again; the two working days start again.
      </p>
    </Section>
  )
}

function ReviewsPanel({ d, run }: { d: Detail; run: Run }) {
  return (
    <Section id="sec-steps" title="The four steps">
      <p className="text-xs text-muted-foreground">Maker and checker are different people at every step: nobody may approve two steps of one separation.</p>
      <div className="space-y-2">{d.reviews.map((r) => <StepRow key={r.step} d={d} r={r} run={run} />)}</div>
    </Section>
  )
}

function StepRow({ d, r, run }: { d: Detail; r: Review; run: Run }) {
  const decide = useDecideSeparationStep<{ ok: boolean }>()
  const [note, setNote] = useState('')
  const [det, setDet] = useState<Record<string, string | boolean>>({})
  const mine = r.assignee_id === d.me && r.status !== 'approved'
  // What step 1 still needs, the same list as "What is left", less the UI-19
  // reason (Luyanda's) and step 1 itself.
  const left1 = failing(d.issue_gates).filter((g) => !['ui19', 'step1'].includes(g.key)).length
  const others = (n: number[]) => n.every((k) => d.reviews.find((x) => x.step === k)?.status === 'approved')
  const can = mine && (r.step === 1 ? d.case.status === 'preparing'
    : d.case.status === 'issued' && (r.step !== 4 || others([2, 3])))
  const details = () => {
    if (r.step === 2) return { ...det, minutes: Number(det.minutes ?? 0) }
    return det
  }
  return (
    <div id={`sec-step-${r.step}`} className={`scroll-mt-4 rounded-md border p-2 ${can ? 'border-2 border-[hsl(var(--brand))]' : 'border-border'}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium text-foreground">Step {r.step}. {r.title}</span>
        {can && <Chip tone="amber">Your turn</Chip>}
        <span className="text-muted-foreground">{r.assignee ?? 'nobody assigned'}{r.due_on ? `, due ${formatDay(r.due_on)}` : ''}</span>
        <Chip tone={r.status === 'approved' ? 'green' : r.status === 'returned' ? 'red' : 'neutral'}>
          {r.status === 'approved' ? `Approved by ${r.decided_by}, ${formatWhen(r.decided_at)}` : r.status === 'returned' ? 'Returned' : 'Pending'}
        </Chip>
        {!r.figures_hash_ok && <Chip tone="red" icon={<AlertTriangle className="h-3 w-3" />}>Figures changed after approval</Chip>}
      </div>
      {r.note && <p className="mt-1 text-xs text-muted-foreground">{r.note}</p>}
      {can && (
        <div className="mt-2 space-y-2">
          {r.step === 2 && (
            <div className="grid gap-2 sm:grid-cols-3">
              <Input className="h-8 text-xs" type="date" title="Spoke to the driver alone on" onChange={(e) => setDet({ ...det, spoke_alone_on: e.target.value })} />
              <Input className="h-8 text-xs" type="number" placeholder="Minutes alone (at least 5)" onChange={(e) => setDet({ ...det, minutes: e.target.value })} />
              <Input className="h-8 text-xs" placeholder="What the driver said" onChange={(e) => setDet({ ...det, driver_said: e.target.value })} />
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" onChange={(e) => setDet({ ...det, not_pressured: e.target.checked })} /> The driver was not pressured</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" onChange={(e) => setDet({ ...det, hearing_route_open: e.target.checked })} /> The hearing route stays open</label>
              <NativeSelect className="h-8 text-xs" defaultValue="" onChange={(e) => setDet({ ...det, rehire: e.target.value })}>
                <option value="">Rehire eligibility</option><option value="yes">Yes</option><option value="review">Review</option><option value="no">No</option>
              </NativeSelect>
              <Input className="h-8 text-xs sm:col-span-3" placeholder="Rehire reason (needed unless yes)" onChange={(e) => setDet({ ...det, rehire_reason: e.target.value })} />
            </div>
          )}
          {r.step === 3 && (
            <div className="flex flex-wrap gap-3 text-xs">
              <label className="flex items-center gap-2"><input type="checkbox" onChange={(e) => setDet({ ...det, lines_checked: e.target.checked })} /> Every 3.1 line checked against payroll and SimplePay</label>
              <label className="flex items-center gap-2"><input type="checkbox" onChange={(e) => setDet({ ...det, arrears_checked: e.target.checked })} /> The arrears line checked</label>
              <label className="flex items-center gap-2"><input type="checkbox" onChange={(e) => setDet({ ...det, annexure_checked: e.target.checked })} /> Annexure A checked against the cap, the floor and the write offs</label>
            </div>
          )}
          {r.step === 4 && <p className="text-xs text-muted-foreground">Read the review summary first. Approving sends the agreement to the driver through SignNow at once: he signs, then you.</p>}
          <div className="flex flex-wrap gap-2">
            <Input className="h-8 flex-1 text-xs" placeholder={r.step === 1 ? 'Note' : 'Note (required to return it)'} value={note} onChange={(e) => setNote(e.target.value)} />
            <Button size="sm" disabled={r.step === 1 && left1 > 0} onClick={() => void run(`Step ${r.step} approved`, () => decide.trigger({ id: d.case.id, step: r.step, decision: 'approved', note, details: details() }).result)}>
              {r.step === 1 ? (left1 > 0 ? `${left1} left in "What is left"` : 'Step 1 is complete: issue the agreement') : 'Approve'}
            </Button>
            {r.step > 1 && <Button size="sm" variant="outline" onClick={() => void run(`Step ${r.step} returned`, () => decide.trigger({ id: d.case.id, step: r.step, decision: 'returned', note, details: {} }).result)}>Return</Button>}
          </div>
        </div>
      )}
    </div>
  )
}

function Events({ d }: { d: Detail }) {
  if (!d.events?.length) return null
  return (
    <Section title="What happened">
      <ul className="space-y-0.5 text-xs text-muted-foreground">
        {d.events.map((e, i) => <li key={i}>{formatWhen(e.at)}: {e.kind.replace(/_/g, ' ')}{e.by ? `, ${e.by}` : ''}</li>)}
      </ul>
    </Section>
  )
}
